import {
  onWatcherCleanup,
  ref,
  shallowRef,
  triggerRef,
  watch,
  watchEffect,
} from "vue";

export const useLocalStorageRef = <T>(
  key: string,
  opts: {
    broadcast?: boolean;
    parse: (value: string | null) => T;
    stringify: (value: T) => string | null;
  }
) => {
  const [track, trigger] = useTrigger();

  const store = ref<T>(undefined as T);

  // Avoids feedback loop when we try to read the changes from another tab
  watchEffect(() => {
    if ((opts.broadcast ?? true) && !import.meta.env.VITEST) {
      track();

      const listener = (ev: StorageEvent) => {
        if (ev.key === key) {
          trigger();
        }
      };

      window.addEventListener("storage", listener);
      onWatcherCleanup(() => window.removeEventListener("storage", listener));
    }

    {
      let raw: string | null = null;
      try {
        raw = localStorage.getItem(key);
      } catch {
        /* do nothing */
      }

      store.value = opts.parse(raw);
    }

    if (!import.meta.env.VITEST) {
      onWatcherCleanup(
        watch(
          store,
          (value) => {
            const raw = opts.stringify(value);

            try {
              if (raw === null) {
                localStorage.removeItem(key);
              } else {
                localStorage.setItem(key, raw);
              }
            } catch {
              /* do nothing */
            }
          },
          { deep: true }
        )
      );
    }
  });

  return store;
};

const useTrigger = (): [track: () => void, trigger: () => void] => {
  const state = shallowRef();
  return [() => state.value, () => triggerRef(state)];
};
